import { useEffect, useState } from "react";
import Modal from "../../components/modal/modal.component";
import InputComponent from "../../components/input/input.component";
import SelectComponent from "../../components/select/select.component";
import DatepickerComponent from "../../components/datepicker/datepicker.component";
import { Controller, useForm } from "react-hook-form";
import TextareaComponent from "../../components/textarea/textarea.component";
import { to } from "../../polyfill";
import { CustomersService } from "../../services/customers";
import { ExpensesService } from "../../services/operation/expenses";
import { ExpensesManagementService } from "../../services/expenses-management";
import Button from "../../components/button/button.component";
import usePeriod from "../../hooks/period.use";
import AlertPeriod from "../../components/alert-period/alert-period";
import InvalidFeedback from "../../components/invalid-feedback/invalid-feedback.component";
import { UtilsService } from "../../services/utils";

function FormModal({ show, data, setShow }: any) {
  const { currentPeriod } = usePeriod();

  const [customers, setCustomers] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);

  const defaultValues = {
    _id: "",
    period: currentPeriod._id,
    customer: "",
    field: "",
    date: "",
    type: "",
    concept: "",
    amount: 0,
    comments: "",
  };

  const {
    watch,
    setValue,
    reset,
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<typeof defaultValues>({
    mode: "onChange",
    defaultValues,
  });

  const _id = watch('_id');
  const customer = watch("customer");
  const type = watch("type");

  const submit = async (form: typeof defaultValues) => {
    const [error, data] = await to(ExpensesService.save(form));
    if (error) {
      alert(error.message);
    } else {
      setShow(true);
      reset(defaultValues);
    }
  };

  const getCustomers = async () => {
    const data = await CustomersService.list();
    setCustomers(data.filter((d: any) => d.status));
  };

  const getFields = async (field: string) => {
    const data = await CustomersService.fields(field);
    setFields(data.filter((d: any) => d.status));
  };
  const getConcepts = async () => {
    const data = await ExpensesManagementService.list();
    setConcepts(data.filter((d: any) => d.status).sort((a: any, b: any) => a.name.localeCompare(b.name)));
  };

  useEffect(() => {
    getCustomers();
    getConcepts();
  }, []);

  useEffect(() => {
    if(data) {
      const exists = fields.find( f => f._id === data.field._id);
      setValue("field", exists ? data.field._id : "");
    } else {
      setValue("field", "");
    }
    
  }, [data, fields]);

  useEffect(() => {
    setValue("customer", "");
  }, [customers]);

  useEffect(() => {
    if(data) {
      const exists = concepts.find( c => c._id === data.concept._id);
      setValue("concept", exists ? data.concept._id: "");
    } else {
      setValue("concept", "");
    }
  }, [data, type, concepts]);

  useEffect(() => {
    if (customer) {
      getFields(customer);
    }
  }, [customer]);

  useEffect(() => {
    if (data) {
      reset({
        _id: data._id,
        period: currentPeriod._id,
        customer: data.customer._id,
        field: data.field._id,
        date: UtilsService.dateFormat(data.date, "YYYY-MM-DD"),
        type: data.type,
        concept: data.concept._id,
        amount: data.amount,
        comments: data.comments,
      });
    }

    if(!show) {
      reset(defaultValues);
    }
  }, [data, show]);

  return (
    <Modal show={show}>
      <div className="modal-header">
        <h3 className="modal-title">{ !_id ? 'Agregar': 'Editar'} gasto</h3>
        <div
          className="btn btn-icon btn-sm btn-active-light-primary ms-2"
          onClick={setShow}
        >
          <i className="ki-duotone ki-cross fs-1">
            <span className="path1" />
            <span className="path2" />
          </i>
        </div>
      </div>
      <div className="modal-body">
        <div className="mb-5">
          <AlertPeriod />
        </div>

        <div className="mb-5">
          <Controller
            name="date"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <DatepickerComponent
                  label="Fecha *"
                  errors={error}
                  onChange={onChange}
                  value={value}
                />
                <InvalidFeedback error={error} />
              </>
            )}
          />
        </div>

        <div className="mb-5">
          <SelectComponent
            label="Productor *"
            data={customers.map((row) => ({ value: row._id, text: row.name }))}
            {...register("customer", { required: true })}
            errors={errors.customer}
          />
        </div>
        <div className="mb-5">
          <SelectComponent
            label="Huerta *"
            data={fields.map((row) => ({ value: row._id, text: row.name }))}
            {...register("field", { required: true })}
            errors={errors.field}
          />
        </div>

        <div className="mb-5">
          <SelectComponent
            label="Tipo  *"
            data={[
              {
                value: "compra",
                text: "Compra",
              },
              {
                value: "gasto",
                text: "Gasto",
              },
            ]}
            {...register("type", { required: true })}
            errors={errors.type}
          />
        </div>
        <div className="mb-5">
          <SelectComponent
            label="Concepto  *"
            data={concepts
              .filter((c) => c.category === type)
              .map((row) => ({ value: row._id, text: row.name }))}
            {...register("concept", { required: true })}
            errors={errors.concept}
          />
        </div>

        <div className="mb-5">
          <InputComponent
            label="Monto *"
            {...register("amount", { required: true, pattern: {
              value: /^\d+(\.\d{1,2})?$/,
              message:
                "Solo se permiten números y un máximo de dos decimales",
            }, })}
            errors={errors.amount}
          />
        </div>

        <div className="mb-5">
          <TextareaComponent
            label="Observaciones *"
            {...register("comments", { required: true })}
            errors={errors.comments}
          />
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-light" type="button" onClick={setShow}>
          Cancelar
        </button>
        <Button
          className="btn btn-primary"
          disabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(submit)}
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
}

export default FormModal;
